import type {
  ApprovedFormulaDefinition,
  ApprovedFormulaGoldenTrace,
  ApprovedFormulaImplementationDigestInput,
  ApprovedFormulaResult,
  ApprovedFormulaStatusBand,
} from './approved-formulas';

/**
 * #5345 Story 6.1 — the four target meanings a stated number can carry, never
 * aliased to one another. Against a 12% baseline and a stated 15:
 *
 *   `margin_level_pct`             reach 15% margin        (absolute level)
 *   `margin_relative_increase_pct` 12% x (1 + 15/100)      = 13.8%, +180 bps
 *   `margin_point_increase`        12% + 15                = 27%, +1,500 bps
 *   `dollar_increase_pct`          +15% profit dollars
 *
 * The relative member is the one that was missing: a reader who says "15%
 * margin growth" means 13.8%, and every reading of that sentence used to
 * collapse onto the absolute level or onto nothing at all.
 */
export type FinanceEbitdaTargetType =
  | 'dollar_increase_pct'
  | 'margin_level_pct'
  | 'margin_relative_increase_pct'
  | 'margin_point_increase';

export const FINANCE_EBITDA_TARGET_TYPES: readonly FinanceEbitdaTargetType[] = Object.freeze([
  'dollar_increase_pct',
  'margin_level_pct',
  'margin_relative_increase_pct',
  'margin_point_increase',
] as const);

export function isFinanceEbitdaTargetType(value: unknown): value is FinanceEbitdaTargetType {
  return (FINANCE_EBITDA_TARGET_TYPES as readonly unknown[]).includes(value);
}

export type FinanceEbitdaTargetResolveInput = Readonly<{
  targetType: FinanceEbitdaTargetType;
  targetValue: number;
  ebitdaUsd: number;
  ebitdaMarginPct: number;
  revenueUsd: number;
  reportingCutAsOf?: string;
}>;

export type FinanceEbitdaTargetResolveOutput = Readonly<{
  ebitdaTargetUsd: number;
  targetMarginPct: number | null;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type FinanceMarginTargetType = Exclude<FinanceEbitdaTargetType, 'dollar_increase_pct'>;

type ApprovedFormulaNumericHelpers = Readonly<{
  isFiniteNumber(value: unknown): value is number;
  roundTo(value: number, digits: number): number;
  marginStatusBand(marginPct: number): ApprovedFormulaStatusBand;
  positiveUsdStatusBand(valueUsd: number): ApprovedFormulaStatusBand;
}>;

let isFiniteNumber: ApprovedFormulaNumericHelpers['isFiniteNumber'];
let roundTo: ApprovedFormulaNumericHelpers['roundTo'];
let marginStatusBand: ApprovedFormulaNumericHelpers['marginStatusBand'];
let positiveUsdStatusBand: ApprovedFormulaNumericHelpers['positiveUsdStatusBand'];

export function configureFinanceEbitdaTargetResolve(
  dependencies: ApprovedFormulaNumericHelpers
): void {
  ({ isFiniteNumber, roundTo, marginStatusBand, positiveUsdStatusBand } = dependencies);
}

type FinanceEbitdaTargetResolveRegistration = Readonly<{
  expectedContextKind: string;
  approvedSourceSystemRefs: readonly string[];
  implementationDigestInputs: readonly ApprovedFormulaImplementationDigestInput[];
  execute: typeof executeFinanceEbitdaTargetResolve;
}>;

const FINANCE_MARGIN_TARGET_LEVELS: Readonly<
  Record<FinanceMarginTargetType, (baselineMarginPct: number, parameterPct: number) => number>
> = Object.freeze({
  margin_level_pct: (_baselineMarginPct, parameterPct) => parameterPct,
  margin_relative_increase_pct: (baselineMarginPct, parameterPct) =>
    baselineMarginPct * (1 + parameterPct / 100),
  margin_point_increase: (baselineMarginPct, parameterPct) => baselineMarginPct + parameterPct,
});

/** Shared target mathematics; the caller supplies the explicitly selected baseline. */
export function financeMarginTargetLevel(
  type: string,
  baseline: number,
  value: number
): number | undefined {
  return Object.prototype.hasOwnProperty.call(FINANCE_MARGIN_TARGET_LEVELS, type)
    ? FINANCE_MARGIN_TARGET_LEVELS[type as FinanceMarginTargetType](baseline, value)
    : undefined;
}

function financeEbitdaTargetInputIssue(
  input: FinanceEbitdaTargetResolveInput
): ApprovedFormulaResult<FinanceEbitdaTargetResolveOutput> | null {
  if (
    !isFiniteNumber(input?.targetValue) ||
    !isFiniteNumber(input?.ebitdaUsd) ||
    !isFiniteNumber(input?.ebitdaMarginPct) ||
    !isFiniteNumber(input?.revenueUsd)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'targetValue, ebitdaUsd, ebitdaMarginPct, and revenueUsd must be finite values',
    };
  }
  if (input.revenueUsd <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'revenueUsd must be greater than zero',
    };
  }
  return null;
}

export function executeFinanceEbitdaTargetResolve(
  input: FinanceEbitdaTargetResolveInput
): ApprovedFormulaResult<FinanceEbitdaTargetResolveOutput> {
  const issue = financeEbitdaTargetInputIssue(input);
  if (issue) return issue;

  if (input.targetType === 'dollar_increase_pct') {
    const ebitdaTargetUsd = roundTo(input.ebitdaUsd * (1 + input.targetValue / 100), 2);
    return {
      status: 'ok',
      output: Object.freeze({
        ebitdaTargetUsd,
        targetMarginPct: null,
        statusBand: positiveUsdStatusBand(ebitdaTargetUsd),
      }),
    };
  }

  const level = financeMarginTargetLevel(
    input.targetType,
    input.ebitdaMarginPct,
    input.targetValue
  );
  const targetMarginPct =
    level === undefined || input.targetType === 'margin_level_pct' ? level : roundTo(level, 4);
  if (targetMarginPct === undefined || !isFiniteNumber(targetMarginPct)) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: `targetType must be one of ${FINANCE_EBITDA_TARGET_TYPES.join(', ')}`,
    };
  }

  const ebitdaTargetUsd = roundTo(input.revenueUsd * (targetMarginPct / 100), 2);
  return {
    status: 'ok',
    output: Object.freeze({
      ebitdaTargetUsd,
      targetMarginPct,
      statusBand: marginStatusBand(targetMarginPct),
    }),
  };
}

const FINANCE_EBITDA_TARGET_RESOLVE_METADATA = Object.freeze({
  formulaId: 'ebitda_target_resolve_v1',
  version: '1.0.0',
  displayName: 'Finance EBITDA target resolve',
  description:
    'Resolves explicit EBITDA target framing into target EBITDA dollars without silently inferring a target type.',
  lifecycle: 'code_defined' as const,
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'finance_ebitda_target_resolve_input_v1',
  outputSchemaVersion: 'finance_ebitda_target_resolve_output_v1',
});

const FINANCE_EBITDA_TARGET_RESOLVE_GOLDEN_TRACES: readonly ApprovedFormulaGoldenTrace<
  FinanceEbitdaTargetResolveInput,
  FinanceEbitdaTargetResolveOutput
>[] = Object.freeze([
  Object.freeze({
    traceId: 'finance_ebitda_target_resolve:margin-level:v1',
    input: Object.freeze({
      targetType: 'margin_level_pct' as const,
      targetValue: 15,
      ebitdaUsd: 110000,
      ebitdaMarginPct: 11,
      revenueUsd: 1000000,
      reportingCutAsOf: '2026-05-31',
    }),
    expected: Object.freeze({
      status: 'ok' as const,
      output: Object.freeze({
        ebitdaTargetUsd: 150000,
        targetMarginPct: 15,
        statusBand: 'sufficient' as const,
      }),
    }),
  }),
  Object.freeze({
    traceId: 'finance_ebitda_target_resolve:dollar-increase:v1',
    input: Object.freeze({
      targetType: 'dollar_increase_pct' as const,
      targetValue: 15,
      ebitdaUsd: 110000,
      ebitdaMarginPct: 11,
      revenueUsd: 1000000,
      reportingCutAsOf: '2026-05-31',
    }),
    expected: Object.freeze({
      status: 'ok' as const,
      output: Object.freeze({
        ebitdaTargetUsd: 126500,
        targetMarginPct: null,
        statusBand: 'sufficient' as const,
      }),
    }),
  }),
  Object.freeze({
    traceId: 'finance_ebitda_target_resolve:margin-relative-increase:v1',
    input: Object.freeze({
      targetType: 'margin_relative_increase_pct' as const,
      targetValue: 15,
      ebitdaUsd: 120000,
      ebitdaMarginPct: 12,
      revenueUsd: 1000000,
      reportingCutAsOf: '2026-05-31',
    }),
    expected: Object.freeze({
      status: 'ok' as const,
      output: Object.freeze({
        ebitdaTargetUsd: 138000,
        targetMarginPct: 13.8,
        statusBand: 'constrained' as const,
      }),
    }),
  }),
  Object.freeze({
    traceId: 'finance_ebitda_target_resolve:margin-point-increase:v1',
    input: Object.freeze({
      targetType: 'margin_point_increase' as const,
      targetValue: 15,
      ebitdaUsd: 120000,
      ebitdaMarginPct: 12,
      revenueUsd: 1000000,
      reportingCutAsOf: '2026-05-31',
    }),
    expected: Object.freeze({
      status: 'ok' as const,
      output: Object.freeze({
        ebitdaTargetUsd: 270000,
        targetMarginPct: 27,
        statusBand: 'sufficient' as const,
      }),
    }),
  }),
]);

export function createFinanceEbitdaTargetResolveDefinition(
  registration: FinanceEbitdaTargetResolveRegistration
): ApprovedFormulaDefinition<FinanceEbitdaTargetResolveInput, FinanceEbitdaTargetResolveOutput> {
  return Object.freeze({
    ...FINANCE_EBITDA_TARGET_RESOLVE_METADATA,
    expectedContextKind: registration.expectedContextKind,
    approvedSourceSystemRefs: registration.approvedSourceSystemRefs,
    implementationDigestInputs: registration.implementationDigestInputs,
    goldenTraces: FINANCE_EBITDA_TARGET_RESOLVE_GOLDEN_TRACES,
    execute: registration.execute,
  });
}
